import FormField from 'components/AddLocation/FormField';
import SectionWrapper from 'components/AddLocation/SectionWrapper';
import AppButton from 'components/App/Button';
import CoordinateMap from 'components/App/CoordinateMap';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import { useFormik } from 'formik';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import React, { createRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import * as Yup from 'yup';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';

import InputNumberValidation from 'components/App/InputNumberValidation';
import 'flatpickr/dist/themes/material_blue.css';
import { Pencil } from 'lucide-react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import slugify from 'slugify';
import {
  actionGetDiscoveryCategories as actionGetDiscoveryCategoriesAction,
  actionGetDistrictList as actionGetDistrictListAction,
  actionGetProvinceList as actionGetProvinceListAction,
  actionGetWardList as actionGetWardListAction,
} from 'store/actions';
import {
  actionGetDiscoveryProvinces as actionGetDiscoveryProvincesAction,
  actionCleanupDiscoveryProvinces as actionCleanupDiscoveryProvincesAction,
} from 'store/discover-province/actions';
import useDiscoveryCreateStore from 'store/zustand/useDiscoveryCreateStore';
import styles from './AddDiscoveryPlaceInfo.module.scss';

function SlugInput(props) {
  const { children, defaultValue } = props;
  const [isDisabled, setIsDisabled] = useState(defaultValue);
  return children({ isDisabled, setIsDisabled });
}

// eslint-disable-next-line no-unused-vars
async function getCoordinates(province, district, ward) {
  // 1. Tạo chuỗi địa chỉ (Cần đảm bảo tên tiếng Việt không dấu được mã hóa URL)
  const fullAddress = `${ward}, ${district}, ${province}, Vietnam`;
  const encodedAddress = encodeURIComponent(fullAddress);

  // 2. Định nghĩa URL của API (Ví dụ dùng Nominatim)
  const apiUrl = `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`;

  try {
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.length > 0) {
      // 3. Trích xuất tọa độ
      const latitude = data[0].lat;
      const longitude = data[0].lon;

      return { lat: latitude, lon: longitude };
    }
    return null;
  } catch (error) {
    return null;
  }
}

export const placeInfoRef = createRef();

const optionFlatpickr = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  minuteIncrement: 1,
  time_24hr: true,
};

const validationSchema = Yup.object().shape({
  province: Yup.string().required('Vui lòng chọn Tỉnh/Thành phố'),
  district: Yup.string().required('Vui lòng chọn Quận/Huyện'),
  ward: Yup.string().required('Vui lòng chọn Phường/Xã'),
  address: Yup.string()
    .max(100, 'Địa chỉ không được vượt quá 100 ký tự')
    .required('Vui lòng nhập địa chỉ'),
  coordinates: Yup.string()
    .max(100, 'Định vị không được vượt quá 100 ký tự')
    .required('Vui lòng nhập định vị')
    .test(
      'is-valid-coordinates',
      'Định vị không hợp lệ', (value) => {
        if (!value) return false;
        const [latitude, longitude] = value.split(',');
        return latitude
          && longitude
          && !Number.isNaN(latitude.trim())
          && !Number.isNaN(longitude.trim());
      }),
  name: Yup.string()
    .max(100, 'Tên không được vượt quá 100 ký tự')
    .required('Vui lòng nhập tên điểm khám phá'),
  nameEN: Yup.string(),
  // .max(100, 'Tên không được vượt quá 100 ký tự')
  // .required('Vui lòng nhập tên điểm khám phá'),
  slug: Yup.string()
    .min(1, 'Slug không được để trống')
    .required('Vui lòng nhập slug'),
  discoverProvince: Yup.string().required('Vui lòng chọn điểm đến'),
  discoverCategories: Yup.array().test(
    'category_required',
    'Vui lòng chọn hạng mục',
    (value) => value?.length > 0,
  ),
  serviceHoursFrom: Yup.string().required('Vui lòng chọn giờ phục vụ từ'),
  serviceHoursTo: Yup.string().required('Vui lòng chọn giờ phục vụ đến'),
  priceRangeFrom: Yup.string(),
  description: Yup.string()
    .required('Vui lòng nhập mô tả')
    .max(2000, 'Mô tả không được vượt quá 2000 ký tự'),
  descriptionEN: Yup.string()
    .max(2000, 'Mô tả không được vượt quá 2000 ký tự'),
  contactName: Yup.string()
    .max(100, 'Tên không được vượt quá 100 ký tự'),
  contactEmail: Yup.string()
    .transform((value) => (value === '' ? undefined : value))
    .email('Email không hợp lệ'),
  contactPhone: Yup.string(),
});

function AddDiscoveryPlaceInfo(props) {
  const {
    onNext,
    // step,
    provinces,
    districts,
    categories,
    wards,
    actionGetProvinceList,
    actionGetDistrictList,
    actionGetWardList,
    actionGetDiscoveryCategories,
    discoveryProvinces,
    actionGetDiscoveryProvinces,
    actionCleanupDiscoveryProvinces,
  } = props;
  const { id } = useParams();
  const isEdit = !!id;

  const { formData, setFormData } = useDiscoveryCreateStore();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const mapRef = useRef(null);

  const validation = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      ...omit(formData, ['isInfoValid', 'isImagesValid']),
    },
    validationSchema,
    onSubmit: (values) => {
      setFormData(values);
      onNext();
    },
  });

  const mapValidation = useFormik({
    initialValues: {
      coordinates: validation.values.coordinates || '10.762622,106.660172',
    },
    onSubmit: () => { },
  });

  const handleTimeChange = React.useCallback(
    (dates, dateStr, instance) => {
      const { name } = instance.input;
      validation.setFieldValue(name, dateStr);
    },
    [validation],
  );

  // const handleSelectChange = React.useCallback(
  //   (fieldName) => (selectedOption) => {
  //     validation.setFieldValue(fieldName, selectedOption?.value || '');
  //   },
  //   [validation],
  // );

  const handleInputChange = React.useCallback(
    (e) => {
      validation.handleChange(e);
    },
    [validation],
  );

  const handleGoogleMapClick = React.useCallback(() => {
    const currentLocation = validation.values.coordinates || '10.762622,106.660172';
    mapValidation.setFieldValue('coordinates', currentLocation);
    setIsMapModalOpen(true);
  }, [validation.values.coordinates, mapValidation]);

  const handleMapModalClose = React.useCallback(() => {
    setIsMapModalOpen(false);
  }, []);

  const handleMapCoordinateSelect = React.useCallback(() => {
    const { coordinates } = mapValidation.values;
    if (coordinates) {
      validation.setFieldValue('coordinates', coordinates);
    }
    setIsMapModalOpen(false);
  }, [mapValidation.values, validation]);

  const formattedProvinces = React.useMemo(() => provinces
    ?.map((province) => ({
      ...province,
      label: province.name,
      value: province.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)), [provinces]);

  const formattedCategories = React.useMemo(() => (categories?.data ?? [])
    .filter((category) => category.isPublished)
    .map((category) => ({
      ...category,
      label: category.name,
      value: category._id,
    })).sort((a, b) => a.label.localeCompare(b.label)), [categories?.data]);

  const formattedDistricts = React.useMemo(() => districts?.map((district) => ({
    ...district,
    label: district.name,
    value: district.id,
  })).sort((a, b) => a.label.localeCompare(b.label)), [districts]);

  const formattedWards = React.useMemo(() => wards?.map((ward) => ({
    ...ward,
    label: ward.name,
    value: ward.id,
  })).sort((a, b) => a.label.localeCompare(b.label)), [wards]);

  const formattedDiscoveryProvinces = React.useMemo(() => {
    const list = discoveryProvinces?.data || [];
    return list
      .map((p) => ({
        ...p,
        label: p.name,
        value: p._id,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [discoveryProvinces]);

  const onChangeAddressOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  useEffect(() => {
    // sync form data to store
    setFormData({ isInfoValid: validation.isValid });
  }, [setFormData, validation.isValid]);

  React.useEffect(() => {
    actionGetProvinceList({});
  }, []);

  React.useEffect(() => {
    if (validation.values.province) {
      actionGetDistrictList({ provinces: validation.values.province });
    }
  }, [validation.values.province]);

  React.useEffect(() => {
    if (validation.values.district && validation.values.province) {
      actionGetWardList({
        provinces: validation.values.province,
        districts: validation.values.district,
      });
    }
  }, [validation.values.district]);

  React.useEffect(() => {
    actionGetDiscoveryCategories({});
    actionGetDiscoveryProvinces({ limit: 1000 });
    return () => {
      actionCleanupDiscoveryProvinces();
    };
  }, [actionGetDiscoveryCategories, actionGetDiscoveryProvinces, actionCleanupDiscoveryProvinces]);

  useImperativeHandle(placeInfoRef, () => ({
    saveData: () => setFormData(validation.values),
  }));

  return (
    <form className={styles.Component} onSubmit={validation.handleSubmit}>
      <SectionWrapper title="Điểm khám phá tọa lạc ở đâu?">
        <Row>
          <Col md={4} className="mb-4">
            <FormField label="Tỉnh/ Thành phố" isRequired>
              <AppSelectValidation
                name="province"
                placeholder="Chọn Tỉnh/ Thành phố"
                options={formattedProvinces}
                validation={validation}
                onChange={(e) => {
                  onChangeAddressOption(e);
                  validation.setFieldValue('district', '', false);
                  validation.setFieldValue('ward', '', false);
                }}
                value={validation.values.province}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Quận/ Huyện" isRequired>
              <AppSelectValidation
                name="district"
                placeholder="Chọn Quận/ Huyện"
                options={formattedDistricts}
                validation={validation}
                onChange={(e) => {
                  onChangeAddressOption(e);
                  validation.setFieldValue('ward', '', false);
                }}
                value={validation.values.district}
                isDisabled={!validation.values.province}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Phường/ Xã" isRequired>
              <AppSelectValidation
                name="ward"
                placeholder="Chọn Phường/ Xã"
                options={formattedWards}
                validation={validation}
                onChange={onChangeAddressOption}
                value={validation.values.ward}
                isDisabled={!validation.values.district}
              />
            </FormField>
          </Col>

          <Col md={12} className="mb-4">
            <FormField label="Địa chỉ" isRequired>
              <AppInputValidation
                name="address"
                type="text"
                placeholder="Nhập địa chỉ"
                validation={validation}
                limitCharacter={100}
                isCounterOnInput
                isCustomOnChange
                onChange={handleInputChange}
              />
            </FormField>
          </Col>

          <Col md={12} className="mb-4">
            <FormField label="Định vị" isRequired>
              <div className={styles.LocationInputWrapper}>
                <AppInputValidation
                  name="coordinates"
                  type="text"
                  placeholder="Nhập định vị"
                  validation={validation}
                  limitCharacter={100}
                  isCounterOnInput
                  isCustomOnChange
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className={styles.GoogleMapLink}
                  onClick={handleGoogleMapClick}
                >
                  Chọn trên Google Map
                </button>
              </div>
            </FormField>
          </Col>
        </Row>
      </SectionWrapper>

      <SectionWrapper title="Tạo điểm khám phá">
        <Row>
          <Col md={6} className="mb-4">
            <FormField label="Tên điểm khám phá - VI" isRequired>
              <AppInputValidation
                name="name"
                type="text"
                placeholder="Nhập tên tuỳ chọn"
                validation={validation}
                limitCharacter={100}
                isCounterOnInput
                isCustomOnChange
                subTitle="Tên này sẽ được hiển thị tới khách khi họ tìm kiếm điểm khám phá trên bookese.com"
                onChange={(e) => {
                  validation.setFieldValue('name', e.target.value);

                  if (!isEdit) {
                    validation.setFieldValue('slug', slugify(e.target.value, '-'));
                  }
                }}
              />
            </FormField>
          </Col>

          <Col md={6} className="mb-4">
            <FormField label="Tên điểm khám phá - EN">
              <AppInputValidation
                name="nameEN"
                type="text"
                placeholder="Nhập tên tuỳ chọn"
                validation={validation}
                limitCharacter={100}
                isCounterOnInput
                isCustomOnChange
                onChange={handleInputChange}
              />
            </FormField>
          </Col>

          <Col md={6} className="mb-4">
            <FormField label="Slug" isRequired>
              <SlugInput defaultValue>
                {({ isDisabled, setIsDisabled }) => (
                  <div className="position-relative">
                    <AppInputValidation
                      name="slug"
                      type="text"
                      placeholder="Nhập slug"
                      validation={validation}
                      isCustomOnChange
                      onChange={(e) => {
                        validation.setFieldValue(
                          'slug',
                          slugify(e.target.value, '-').toLowerCase(),
                        );
                      }}
                      isDisabled={isDisabled}
                      style={{ paddingRight: '30px' }}
                    />

                    <button
                      type="button"
                      onClick={() => setIsDisabled((prev) => !prev)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px 10px',
                      }}
                    >
                      <Pencil width={16} height={16} />
                    </button>
                  </div>
                )}
              </SlugInput>
            </FormField>
          </Col>

          <Col md={6} className="mb-4">
            <FormField label="Điểm đến" isRequired>
              <AppSelectValidation
                name="discoverProvince"
                placeholder="Chọn điểm đến"
                options={formattedDiscoveryProvinces}
                validation={validation}
                onChange={onChangeAddressOption}
                value={validation.values.discoverProvince}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Hạng mục" isRequired>
              <AppSelectValidation
                isMulti
                name="discoverCategories"
                placeholder="Chọn hạng mục"
                options={formattedCategories}
                validation={validation}
                value={validation.values.discoverCategories}
                onChangeMulti={(selected) => {
                  validation.setFieldValue(
                    'discoverCategories',
                    selected,
                    true);
                }}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Giờ phục vụ Từ" isRequired>
              <Flatpickr
                name="serviceHoursFrom"
                className={styles.TimePicker}
                defaultValue={validation.values.serviceHoursFrom}
                options={optionFlatpickr}
                onChange={handleTimeChange}
                value={validation.values.serviceHoursFrom}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Đến" isRequired>
              <Flatpickr
                name="serviceHoursTo"
                className={styles.TimePicker}
                defaultValue={validation.values.serviceHoursTo}
                options={optionFlatpickr}
                onChange={handleTimeChange}
                value={validation.values.serviceHoursTo}
              />
            </FormField>
          </Col>

          <Col md={12} className="mb-4">
            <FormField label="Khoảng giá từ">
              <InputNumberValidation
                name="priceRangeFrom"
                type="text"
                placeholder="Nhập số tiền tối thiểu"
                validation={validation}
                showCurrency="VNĐ"
                isCustomOnChange
                formatOptions={{
                  onValueChange: (values) => {
                    validation.setFieldValue('priceRangeFrom', values.value);
                  },
                }}
              />
            </FormField>
          </Col>

          <Col md={6} className="mb-4">
            <FormField label="Mô tả điểm khám phá - VI" isRequired>
              <AppInputValidation
                name="description"
                type="textarea"
                placeholder="Nhập mô tả"
                validation={validation}
                limitCharacter={2000}
                isCounterOnInput
                isCustomOnChange
                onChange={handleInputChange}
                inputClassName="FormField__Textarea"
                style={{ minHeight: '84px' }}
              />
            </FormField>
          </Col>

          <Col md={6} className="mb-4">
            <FormField label="Mô tả điểm khám phá - EN">
              <AppInputValidation
                name="descriptionEN"
                type="textarea"
                placeholder="Nhập mô tả"
                validation={validation}
                limitCharacter={2000}
                isCounterOnInput
                isCustomOnChange
                onChange={handleInputChange}
                inputClassName="FormField__Textarea"
                style={{ minHeight: '84px' }}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Tên người liên hệ">
              <AppInputValidation
                name="contactName"
                type="text"
                placeholder="Nhập Tên người liên hệ chính của chỗ nghỉ"
                validation={validation}
                limitCharacter={100}
                isCounterOnInput
                isCustomOnChange
                onChange={handleInputChange}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Địa chỉ Email">
              <AppInputValidation
                name="contactEmail"
                type="email"
                placeholder="Nhập địa chỉ Email người liên hệ"
                validation={validation}
                isCustomOnChange
                onChange={handleInputChange}
              />
            </FormField>
          </Col>

          <Col md={4} className="mb-4">
            <FormField label="Số điện thoại liên lạc">
              <AppInputValidation
                name="contactPhone"
                type="tel"
                placeholder="Nhập số điện thoại người liên hệ"
                validation={validation}
                isCustomOnChange
                onChange={handleInputChange}
              />
            </FormField>
          </Col>
        </Row>
      </SectionWrapper>

      <div className={styles.ButtonWrapper}>
        <AppButton type="submit" color="primary" classes={styles.NextButton}>
          Bước tiếp theo
          <NextIconSVG className="Icon__NextStep" />
        </AppButton>
      </div>

      <Modal
        fade
        size="lg"
        isOpen={isMapModalOpen}
        toggle={handleMapModalClose}
        className={styles.MapModal}
      >
        <ModalHeader
          toggle={handleMapModalClose}
          className={styles.ModalHeader}
          close={(
            <button
              className={styles.ButtonClose}
              onClick={handleMapModalClose}
              type="button"
            >
              <CloseButtonSvg />
            </button>
          )}
        >
          Chọn địa điểm trên Google Map
        </ModalHeader>
        <ModalBody className={styles.ModalBody}>
          <CoordinateMap
            defaultPosition={mapValidation.values.coordinates}
            validation={mapValidation}
            mapRef={mapRef}
          />
          <div className={styles.ModalActions}>
            <AppButton
              type="button"
              outline
              onClick={handleMapModalClose}
              classes={styles.CancelButton}
            >
              Hủy
            </AppButton>
            <AppButton
              type="button"
              onClick={handleMapCoordinateSelect}
              classes={styles.ConfirmButton}
            >
              Xác nhận
            </AppButton>
          </div>
        </ModalBody>
      </Modal>
    </form>
  );
}

AddDiscoveryPlaceInfo.propTypes = {
  onNext: PropTypes.func.isRequired,
  // step: PropTypes.string.isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  districts: PropTypes.instanceOf(Array).isRequired,
  wards: PropTypes.instanceOf(Array).isRequired,
  actionGetProvinceList: PropTypes.func.isRequired,
  actionGetDistrictList: PropTypes.func.isRequired,
  actionGetWardList: PropTypes.func.isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  actionGetDiscoveryCategories: PropTypes.func.isRequired,
  discoveryProvinces: PropTypes.instanceOf(Object).isRequired,
  actionGetDiscoveryProvinces: PropTypes.func.isRequired,
  actionCleanupDiscoveryProvinces: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  formData: state.formData,
  provinces: state.Address.provinces,
  districts: state.Address.districts,
  wards: state.Address.wards,
  categoriesLoading: state.DiscoverCategory.categoriesLoading,
  categories: state.DiscoverCategory.categories,
  discoveryProvinces: state.DiscoverProvince.provinces,
});

const mapDispatchToProps = (dispatch) => ({
  actionGetProvinceList: (payload) =>
    dispatch(actionGetProvinceListAction(payload)),
  actionGetDistrictList: (payload) =>
    dispatch(actionGetDistrictListAction(payload)),
  actionGetWardList: (payload) =>
    dispatch(actionGetWardListAction(payload)),
  actionGetDiscoveryCategories: (payload) =>
    dispatch(actionGetDiscoveryCategoriesAction(payload)),
  actionGetDiscoveryProvinces: (payload) =>
    dispatch(actionGetDiscoveryProvincesAction(payload)),
  actionCleanupDiscoveryProvinces: () =>
    dispatch(actionCleanupDiscoveryProvincesAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddDiscoveryPlaceInfo);
